import type { ActionReturnType, GlobalState } from '../../types';
import { ManagementProgress } from '../../../types';

import {
  CUSTOM_BG_CACHE_NAME,
  DEBUG,
  DISABLE_REAL_MTPROTO,
  LANG_CACHE_NAME,
  LOCK_SCREEN_ANIMATION_DURATION_MS,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_PROGRESSIVE_CACHE_NAME,
} from '../../../config';
import { updateAppBadge } from '../../../util/appBadge';
import { toCredentialRequestOptions } from '../../../util/browser/passkeys';
import {
  IS_WEBAUTHN_SUPPORTED,
  IS_WEBM_SUPPORTED, MAX_BUFFER_SIZE, PLATFORM_ENV,
} from '../../../util/browser/windowEnvironment';
import * as cacheApi from '../../../util/cacheApi';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import {
  ACCOUNT_SLOT, clearSlotSession, getAccountsInfo, getAccountSlotUrl, getFirstLoggedInAccountSlot,
  storeAccountData,
} from '../../../util/multiaccount';
import { unsubscribe } from '../../../util/notifications';
import { clearEncryptedSession, encryptSession, forgetPasscode } from '../../../util/passcode';
import { parseInitialLocationHash, resetInitialLocationHash, resetLocationHash } from '../../../util/routing';
import { pause } from '../../../util/schedulers';
import {
  clearStoredSession,
  loadStoredSession,
  storeSession,
} from '../../../util/sessions';
import { forceWebsync } from '../../../util/websync';
import {
  callApi, callApiLocal, initApi, setShouldEnableDebugLog,
} from '../../../api/gramjs';
import { initOpenImLogin, logoutOpenIm } from '../../../api/openim/methods/auth';
import { fetchOpenImSelfUser } from '../../../api/openim/methods/friends';
import {
  startOpenImRealtimeBridge, stopOpenImRealtimeBridge,
} from '../../../api/openim/sync/realtimeListeners';
import {
  logoutSession, refreshSession, sendEmailCode, sendSmsCode, submitSignup, verify2fa, verifyEmailCode,
  verifySmsCode,
} from '../../../api/tgimBackend/authMethods';
import {
  clearFeatureFlagsCache, guardFeature, loadFeatureFlags, startFeatureFlagsFocusPoll, TGIM_FEATURE,
} from '../../../api/tgimBackend/featureFlags';
import { TgimBackendError } from '../../../api/tgimBackend/httpClient';
import { applyTgimProfileToApiUser } from '../../../api/tgimBackend/mapProfile';
import { splitPhoneNumberByCountryCode } from '../../../api/tgimBackend/phoneNumber';
import { fetchMyTgimProfile, uploadMyTgimAvatar } from '../../../api/tgimBackend/profile';
import {
  clearTgimSession, ensureTgimSession, hasResumableTgimSession,
  loadTgimSession, storeTgimSession,
} from '../../../api/tgimBackend/session';
import {
  removeGlobalFromCache, removeSharedStateFromCache, serializeGlobal, serializeShared,
} from '../../cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import {
  clearGlobalForLockScreen, updateManagementProgress, updatePasscodeSettings, updateUser, updateUserFullInfo,
  updateUsers,
} from '../../reducers';
import { updateAuth } from '../../reducers/auth';
import { selectUser } from '../../selectors';
import { selectSharedSettings } from '../../selectors/sharedState';
import { destroySharedStatePort } from '../../shared/sharedStateConnector';

let resetStoragePromise: Promise<boolean> | undefined;

addActionHandler('initApi', (global, actions): ActionReturnType => {
  const initialLocationHash = parseInitialLocationHash();
  const {
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    shouldCollectDebugLogs,
    language,
  } = selectSharedSettings(global);

  const hasTestParam = window.location.search.includes('test') || initialLocationHash?.tgWebAuthTest === '1';

  const isTestServer = global.config?.isTestServer;
  const accountsInfo = getAccountsInfo();
  const accountIds = Object.values(accountsInfo)
    .filter((info) => info.isTest === isTestServer)
    .map(({ userId }) => userId)
    .filter(Boolean);

  void initApi(actions.apiUpdate, {
    userAgent: navigator.userAgent,
    platform: PLATFORM_ENV,
    sessionData: loadStoredSession(),
    isWebmSupported: IS_WEBM_SUPPORTED,
    maxBufferSize: MAX_BUFFER_SIZE,
    webAuthToken: initialLocationHash?.tgWebAuthToken,
    dcId: initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash?.tgWebAuthDcId) : undefined,
    mockScenario: initialLocationHash?.mockScenario,
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    langCode: language,
    isTestServerRequested: hasTestParam,
    accountIds,
    hasPasskeySupport: IS_WEBAUTHN_SUPPORTED,
  });

  void setShouldEnableDebugLog(Boolean(shouldCollectDebugLogs));

  // Auth screen feature gates (email/QR) need flags before login completes.
  if (DISABLE_REAL_MTPROTO) {
    void loadFeatureFlags().catch(() => undefined);
  }

  // A page refresh always bounced back to the auth screens, even for an already-logged-in user:
  // nothing here ever re-checked for a persisted tg-im-backend/OpenIM session on boot the way real
  // Telegram's own gramjs session restore does. `hasStoredSession()` (now aware of this session type
  // too, see its own citation) makes the very first render optimistically show the main UI already;
  // this actually re-establishes the OpenIM login behind that -- `currentUserId`, the realtime
  // bridge, feature flags -- and only then flips `auth.state` to `authorizationStateReady`, the
  // exact same sequence a fresh code/2FA login already goes through in `completeOpenImLogin`.
  if (DISABLE_REAL_MTPROTO && hasResumableTgimSession()) {
    void resumeTgimSessionOnBoot();
  }
});

// Bumped on signOut so an in-flight boot resume cannot rewrite session after logout.
let tgimResumeGeneration = 0;

// Network/transport failures (fetch rejects, WASM boot hiccup) vs the OpenIM/backend explicitly
// telling us the token is bad — only the latter should burn the refresh token and wipe the
// session. Conflating the two meant two transient blips in a row (e.g. a flaky connection during
// boot) logged out a user whose tokens were never actually invalid.
export function isLikelyTransientResumeError(err: unknown): boolean {
  if (err instanceof TypeError) return true; // fetch()/network-level rejection
  if (err && typeof err === 'object' && 'name' in err && (err as { name?: unknown }).name === 'AbortError') return true;
  return false;
}

async function resumeTgimSessionOnBoot(): Promise<void> {
  const generation = ++tgimResumeGeneration;
  try {
    // Access may still look valid while OpenIM token is already dead — ensure/refresh first so
    // `initOpenImLogin` gets a fresh openim_token. Aligns with Android/iOS refresh-before-resume.
    const ensured = await ensureTgimSession();
    if (generation !== tgimResumeGeneration) return;
    if (!ensured) {
      handleTgimSessionResumeFailure(new Error('No resumable tgim session'));
      return;
    }
    const global = getGlobal();
    await completeOpenImLogin(global);
  } catch (err) {
    if (generation !== tgimResumeGeneration) return;

    let resumeError: unknown = err;

    // A plain transient failure (network blip, WASM hiccup) does not mean the token is invalid —
    // retry the same login once as-is before considering the token itself suspect.
    if (isLikelyTransientResumeError(resumeError)) {
      try {
        const global = getGlobal();
        await completeOpenImLogin(global);
        return;
      } catch (retryErr) {
        if (generation !== tgimResumeGeneration) return;
        if (isLikelyTransientResumeError(retryErr)) {
          // Still just transient: leave the session alone rather than wiping valid tokens: retry
          // stays available on the next reload/action instead of forcing a fresh login.
          if (DEBUG) {
            // eslint-disable-next-line no-console
            console.error('[auth] Boot resume hit a transient error twice, leaving session intact', retryErr);
          }
          onAuthResumeTransientFailure();
          return;
        }
        resumeError = retryErr;
      }
    }

    // One refresh+retry before wiping localStorage — covers OpenIM token expiry while access JWT
    // still looks fresh (common cause of "refresh page → login screen").
    const existing = loadTgimSession();
    if (existing?.refreshToken) {
      try {
        const next = await refreshSession(existing.refreshToken);
        if (generation !== tgimResumeGeneration) return;
        // Resume always keeps localStorage if refresh_token existed (remember-me path).
        storeTgimSession(next, { rememberMe: true });
        const global = getGlobal();
        await completeOpenImLogin(global);
        return;
      } catch (retryErr) {
        if (generation !== tgimResumeGeneration) return;
        handleTgimSessionResumeFailure(retryErr);
        return;
      }
    }
    handleTgimSessionResumeFailure(resumeError);
  }
}

// Leaves `auth.state` untouched (still whatever it was, typically the pre-boot default) but stops
// the loading spinner so the UI doesn't hang forever; a manual reload naturally retries resume.
function onAuthResumeTransientFailure(): void {
  let global = getGlobal();
  global = updateAuth(global, { isLoading: false });
  setGlobal(global);
}

function handleTgimSessionResumeFailure(err: unknown): void {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.error('[auth] Failed to resume the persisted session on boot', err);
  }
  clearTgimSession();
  let global = getGlobal();
  global = updateAuth(global, { state: 'authorizationStateWaitPhoneNumber' });
  setGlobal(global);
}

addActionHandler('setAuthPhoneNumber', async (global, actions, payload): Promise<void> => {
  const { phoneNumber } = payload;

  if (!DISABLE_REAL_MTPROTO) {
    void callApi('provideAuthPhoneNumber', phoneNumber.replace(/[^\d]/g, ''));
    global = updateAuth(global, { isLoading: true, errorKey: undefined });
    setGlobal(global);
    return;
  }

  global = updateAuth(global, {
    isLoading: true, errorKey: undefined, phoneNumber, tgimEmail: undefined,
  });
  setGlobal(global);

  try {
    const { phoneCodes } = global.countryList;
    const { countryCode, nationalNumber } = splitPhoneNumberByCountryCode(phoneNumber, phoneCodes);
    await sendSmsCode({ countryCode, phoneNumber: nationalNumber });

    global = getGlobal();
    global = updateAuth(global, {
      isLoading: false,
      state: 'authorizationStateWaitCode',
      tgimCountryCode: countryCode,
      tgimNationalNumber: nationalNumber,
      tgimEmail: undefined,
    });
    setGlobal(global);
  } catch (err) {
    global = getGlobal();
    // `tgimFetch` throws `TgimBackendError` for any non-2xx response (rate-limited, tenant/IP
    // rejection, a real backend failure, ...), which used to all collapse into the same
    // "invalid phone number" message regardless of cause -- misleading during testing (e.g. a
    // `RATE_LIMITED` response, hit after a few quick retries of the same number, looked identical
    // to a genuine format error) and just as misleading for a real user.
    const errorCode = err instanceof TgimBackendError
      ? (err.body as { error?: { code?: string } })?.error?.code
      : undefined;
    global = updateAuth(global, {
      isLoading: false,
      errorKey: { key: errorCode === 'RATE_LIMITED' ? 'FloodWait' : 'ErrorPhoneNumberInvalid' },
    });
    setGlobal(global);
  }
});

addActionHandler('setAuthEmail', async (global, actions, payload): Promise<void> => {
  const { email } = payload;
  if (!DISABLE_REAL_MTPROTO) return;

  const normalized = email.trim().toLowerCase();
  if (!normalized.includes('@')) {
    global = updateAuth(global, { errorKey: { key: 'ErrorEmailInvalid' } });
    setGlobal(global);
    return;
  }

  global = updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
    tgimEmail: normalized,
    tgimCountryCode: undefined,
    tgimNationalNumber: undefined,
    phoneNumber: undefined,
  });
  setGlobal(global);

  try {
    await sendEmailCode({ email: normalized });
    global = getGlobal();
    global = updateAuth(global, {
      isLoading: false,
      state: 'authorizationStateWaitCode',
      tgimEmail: normalized,
    });
    setGlobal(global);
  } catch (err) {
    global = getGlobal();
    const errorCode = err instanceof TgimBackendError
      ? (err.body as { error?: { code?: string } })?.error?.code
      : undefined;
    global = updateAuth(global, {
      isLoading: false,
      errorKey: { key: errorCode === 'RATE_LIMITED' ? 'FloodWait' : 'ErrorEmailInvalid' },
    });
    setGlobal(global);
  }
});

addActionHandler('setAuthCode', async (global, actions, payload): Promise<void> => {
  const { code } = payload;

  if (!DISABLE_REAL_MTPROTO) {
    void callApi('provideAuthCode', code);
    global = updateAuth(global, { isLoading: true, errorKey: undefined });
    setGlobal(global);
    return;
  }

  const { tgimCountryCode, tgimNationalNumber, tgimEmail } = global.auth;
  global = updateAuth(global, { isLoading: true, errorKey: undefined });
  setGlobal(global);

  // Distinguishes "the backend rejected this code" from "the code was accepted but bringing up the
  // IM session then failed". Collapsing both into `ErrorCodeInvalid` used to actively mislead: an
  // OpenIM login failure surfaced as "Invalid code, please try again", so a correct code looked
  // wrong and the real fault was invisible.
  let isCodeVerified = false;

  try {
    const result = tgimEmail
      ? await verifyEmailCode({ email: tgimEmail, code })
      : await verifySmsCode({ countryCode: tgimCountryCode!, phoneNumber: tgimNationalNumber!, code });
    isCodeVerified = true;
    global = getGlobal();

    if (result.step === 'need_signup') {
      global = updateAuth(global, {
        isLoading: false,
        state: 'authorizationStateWaitRegistration',
        tgimSignupTicket: result.signup_ticket,
      });
    } else if (result.step === 'need_2fa') {
      global = updateAuth(global, {
        isLoading: false,
        state: 'authorizationStateWaitPassword',
        hint: result.password_hint,
        tgimLoginTicket: result.login_ticket,
      });
    } else {
      persistAuthenticatedSession(global, result);
      await completeOpenImLogin(global);
      global = getGlobal();
    }
    setGlobal(global);
  } catch (err) {
    if (isCodeVerified && DEBUG) {
      // eslint-disable-next-line no-console
      console.error('[auth] Code accepted but starting the IM session failed', err);
    }
    global = getGlobal();
    global = updateAuth(global, {
      isLoading: false,
      errorKey: { key: isCodeVerified ? 'ErrorUnspecified' : 'ErrorCodeInvalid' },
    });
    setGlobal(global);
  }
});

addActionHandler('setAuthPassword', async (global, actions, payload): Promise<void> => {
  const { password } = payload;

  if (!DISABLE_REAL_MTPROTO) {
    void callApi('provideAuthPassword', password);
    global = updateAuth(global, { isLoading: true, errorKey: undefined });
    setGlobal(global);
    return;
  }

  const { tgimLoginTicket } = global.auth;
  global = updateAuth(global, { isLoading: true, errorKey: undefined });
  setGlobal(global);

  try {
    const session = await verify2fa({ loginTicket: tgimLoginTicket!, password });
    persistAuthenticatedSession(global, session);
    await completeOpenImLogin(global);
    global = getGlobal();
    setGlobal(global);
  } catch (err) {
    global = getGlobal();
    global = updateAuth(global, { isLoading: false, errorKey: { key: 'ErrorIncorrectPassword' } });
    setGlobal(global);
  }
});

addActionHandler('loginWithPasskey', async (global, actions, payload): Promise<void> => {
  const passkeyOption = global.auth.passkeyOption;
  if (!passkeyOption) return;

  const credential = await navigator.credentials.get(toCredentialRequestOptions(passkeyOption)).catch((e: unknown) => {
    actions.showNotification({
      message: {
        key: 'PasskeyLoginError',
      },
      tabId: getCurrentTabId(),
    });
  });
  if (!credential) return;

  const publicKeyCredential = credential as PublicKeyCredential;
  callApi('restartAuthWithPasskey', publicKeyCredential.toJSON() as AuthenticationResponseJSON);
});

addActionHandler('uploadProfilePhoto', async (global, actions, payload): Promise<void> => {
  const {
    file, isFallback, isVideo, videoTs, bot,
    tabId = getCurrentTabId(),
  } = payload;

  global = updateManagementProgress(global, ManagementProgress.InProgress, tabId);
  setGlobal(global);

  if (DISABLE_REAL_MTPROTO) {
    try {
      if (!file || bot || isVideo) {
        actions.showNotification({ message: '暂不支持该头像类型', tabId });
        global = getGlobal();
        global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
        setGlobal(global);
        return;
      }
      const profile = await uploadMyTgimAvatar(file);
      global = getGlobal();
      const userId = global.currentUserId;
      if (userId) {
        global = updateUser(global, userId, { avatarBlobUrl: profile.avatar_url || undefined });
      }
      global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
      setGlobal(global);
      actions.loadCurrentUser();
    } catch {
      global = getGlobal();
      global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
      setGlobal(global);
      actions.showNotification({ message: { key: 'ErrorUnspecified' }, tabId });
    }
    return;
  }

  const result = await callApi('uploadProfilePhoto', file, isFallback, isVideo, videoTs, bot);
  if (!result) return;

  global = getGlobal();
  global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
  setGlobal(global);

  const userId = bot?.id ?? global.currentUserId;
  if (!userId) return;

  actions.loadFullUser({ userId });
});

addActionHandler('signUp', async (global, actions, payload): Promise<void> => {
  const { firstName, lastName, photo } = payload;

  if (!DISABLE_REAL_MTPROTO) {
    void callApi('provideAuthRegistration', { firstName, lastName });
    global = updateAuth(global, { isLoading: true, errorKey: undefined });
    setGlobal(global);
    return;
  }

  const { tgimSignupTicket } = global.auth;
  global = updateAuth(global, { isLoading: true, errorKey: undefined });
  setGlobal(global);

  try {
    const result = await submitSignup({ signupTicket: tgimSignupTicket!, firstName, lastName });
    global = getGlobal();
    if (result.step === 'need_2fa') {
      global = updateAuth(global, {
        isLoading: false,
        state: 'authorizationStateWaitPassword',
        hint: result.password_hint,
        tgimLoginTicket: result.login_ticket,
      });
    } else {
      persistAuthenticatedSession(global, result);
      await completeOpenImLogin(global);
      // Android/iOS upload avatar via POST /account/me/avatar after signup — never gramjs
      // `uploadProfilePhoto` (dead under DISABLE_REAL_MTPROTO).
      if (photo) {
        try {
          const profile = await uploadMyTgimAvatar(photo);
          global = getGlobal();
          const userId = global.currentUserId;
          if (userId) {
            const current = selectUser(global, userId);
            if (current) {
              global = updateUsers(global, {
                [userId]: applyTgimProfileToApiUser(current, profile),
              });
              if (profile.bio !== undefined) {
                global = updateUserFullInfo(global, userId, { bio: profile.bio });
              }
              setGlobal(global);
            }
          }
        } catch {
          // Signup already succeeded; avatar can be set later in Edit Profile.
        }
      }
      global = getGlobal();
    }
    setGlobal(global);
  } catch (err) {
    global = getGlobal();
    global = updateAuth(global, { isLoading: false, errorKey: { key: 'ErrorUnexpected' } });
    setGlobal(global);
  }
});

// Shared by `setAuthCode`, `setAuthPassword` and `signUp`: finalizes a successful tg-im-backend
// login by bootstrapping the OpenIM session and flipping auth state to ready. `currentUserId` and
// its `ApiUser` entry in `users.byId` are set together in the same `setGlobal` -- setting
// `currentUserId` on its own first (as this used to) opens a window where a render can find
// `global.users.byId[global.currentUserId]` still `undefined`. A fresh interactive login never hit
// this in practice (the auth screens stay up until `authorizationStateReady`, well after this
// function returns), but resuming a persisted session on boot renders the main UI immediately (see
// `hasStoredSession`), landing squarely in that window and crashing `selectIsCurrentUserPremium`.
// Persists both updates itself instead of returning `global`: once this call is awaited, resuming
// the caller always crosses a tick boundary, and carrying a pre-fetched `global` across it would
// trip the `DEBUG_randomId` staleness guard in `setUntypedGlobal` (see teactn.tsx).
function persistAuthenticatedSession(global: GlobalState, session: Parameters<typeof storeTgimSession>[0]) {
  // Android/iOS always persist tokens when authenticated; Web 「保持登录」controls refresh persistence.
  storeTgimSession(session, { rememberMe: global.auth.rememberMe !== false });
}

async function completeOpenImLogin(global: GlobalState): Promise<void> {
  // Bumped by `signOut` / `resumeTgimSessionOnBoot` — abort if logout raced mid-await.
  const loginGeneration = tgimResumeGeneration;
  const stillCurrent = () => loginGeneration === tgimResumeGeneration && Boolean(loadTgimSession());

  const session = loadTgimSession();
  if (!session) return;

  const { selfUserId } = await initOpenImLogin(session);
  if (!stillCurrent()) return;

  // Subscribed immediately (no `await` in between) so no realtime event can slip through
  // unhandled while this function goes on to fetch the self `ApiUser` below.
  stopOpenImRealtimeBridge();
  startOpenImRealtimeBridge(selfUserId);

  // Feature flags, self user and /account/me are independent after WASM login — run together so
  // the signup "Next" spinner isn't held by three serial round-trips.
  startFeatureFlagsFocusPoll();
  // Soft-fail flags/self-user: a transient /feature-flags or getSelfUser blip must not wipe the
  // persisted session on page reload (that was a common "refresh → login" path).
  const [selfUser, profile] = await Promise.all([
    fetchOpenImSelfUser().catch(() => undefined),
    fetchMyTgimProfile().catch(() => undefined),
    loadFeatureFlags().catch(() => undefined),
  ]).then(([user, me]) => [user, me] as const);

  if (!stillCurrent()) {
    stopOpenImRealtimeBridge();
    return;
  }

  // Android merges GET /account/me (first_name / last_name / avatar_url) onto the OpenIM user —
  // OpenIM only stores a single nickname + faceURL, which collapses 姓/名 and often lacks avatar
  // until /account/me/avatar has synced faceURL.
  global = getGlobal();
  if (selfUser && profile) {
    const hydratedSelf = applyTgimProfileToApiUser(selfUser, profile);
    global = updateUsers(global, { [hydratedSelf.id]: hydratedSelf });
    global = updateUserFullInfo(global, hydratedSelf.id, { bio: profile.bio });
    global = { ...global, currentUserId: selfUserId };
    setGlobal(global);
  } else if (selfUser) {
    global = updateUsers(global, { [selfUser.id]: selfUser });
    global = { ...global, currentUserId: selfUserId };
    setGlobal(global);
  } else {
    // Minimal stub so Main can render; loadCurrentUser will hydrate later.
    global = {
      ...global,
      currentUserId: selfUserId,
      users: {
        ...global.users,
        byId: {
          ...global.users.byId,
          [selfUserId]: {
            id: selfUserId,
            type: 'userTypeRegular',
            firstName: '',
            isSelf: true,
          } as any,
        },
      },
    };
    setGlobal(global);
  }

  if (!stillCurrent()) {
    stopOpenImRealtimeBridge();
    return;
  }

  global = getGlobal();
  // Account switcher needs a slot row (Android uses TG UserConfig; OpenIM uses placeholder dcId).
  const self = global.currentUserId ? global.users.byId[global.currentUserId] : undefined;
  storeAccountData(ACCOUNT_SLOT, {
    userId: selfUserId,
    firstName: self?.firstName,
    lastName: self?.lastName,
    phone: self?.phoneNumber,
    isPremium: self?.isPremium,
  });

  // Under `DISABLE_REAL_MTPROTO` the gramjs worker never emits `connectionStateReady`, so the
  // sync callback in `actions/api/sync.ts` (which gates `loadAllChats`) and every other
  // `connectionState === 'connectionStateReady'` check would stay dead forever — empty chat
  // list, blocked typing indicators, etc. Mark the connection ready once OpenIM login itself
  // succeeded; conversation population still waits on `OnSyncServerFinish` via the realtime bridge.
  // Clear `isSynced` so a re-login after sign-out (or a session resume that left the flag set)
  // always re-runs `sync` → `loadAllChats`.
  global = {
    ...global,
    connectionState: 'connectionStateReady',
    isSynced: false,
    isSyncing: false,
  };
  global = updateAuth(global, { isLoading: false, state: 'authorizationStateReady' });
  setGlobal(global);
}
addActionHandler('returnToAuthPhoneNumber', (global): ActionReturnType => {
  // `restartAuth` only does anything once the real MTProto auth loop is alive and awaiting a
  // promise it set up itself (`onRequestPhoneNumber`/etc in `api/gramjs/methods/auth.ts`) -- a loop
  // that never starts under `DISABLE_REAL_MTPROTO` (the gramjs worker is never instantiated), making
  // `restartAuth` a silent no-op and this whole "switch method" button dead. `auth.state` has to be
  // flipped directly instead, the same way `setAuthPhoneNumber`/`setAuthCode` already bypass the
  // gramjs-driven state machine entirely for this build.
  if (DISABLE_REAL_MTPROTO) {
    return updateAuth(global, {
      state: 'authorizationStateWaitPhoneNumber',
      errorKey: undefined,
    });
  }

  void callApi('restartAuth');

  return updateAuth(global, {
    errorKey: undefined,
  });
});

addActionHandler('goToAuthQrCode', (global, actions): ActionReturnType => {
  // Telegram QR login is stub/hidden under OpenIM — never enter the dead spinner screen.
  if (DISABLE_REAL_MTPROTO) {
    if (guardFeature(TGIM_FEATURE.AUTH_QR, {
      showNotification: ({ message }) => actions.showNotification({ message, tabId: getCurrentTabId() }),
    })) {
      return updateAuth(global, {
        state: 'authorizationStateWaitPhoneNumber',
        errorKey: undefined,
      });
    }
    return updateAuth(global, {
      state: 'authorizationStateWaitPhoneNumber',
      errorKey: undefined,
    });
  }

  void callApi('restartAuthWithQr');

  return updateAuth(global, {
    isLoadingQrCode: true,
    errorKey: undefined,
  });
});

addActionHandler('saveSession', (global, actions, payload): ActionReturnType => {
  if (global.passcode.isScreenLocked) {
    return;
  }

  const { sessionData } = payload;
  if (sessionData) {
    storeSession(sessionData);
  } else {
    clearStoredSession();
  }
});

addActionHandler('signOut', async (global, actions, payload): Promise<void> => {
  if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });
  if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId: getCurrentTabId() });

  // OpenIM/tg-im-backend: without clearing the persisted session, `forceInitApi` /
  // `hasValidTgimSession` on the next boot immediately resumes login — the confirm dialog
  // closes but the user never leaves the app (looks like "logout does nothing").
  if (DISABLE_REAL_MTPROTO) {
    tgimResumeGeneration += 1;
    const session = loadTgimSession();
    stopOpenImRealtimeBridge();
    clearTgimSession();
    clearSlotSession(ACCOUNT_SLOT);
    clearFeatureFlagsCache();
    try {
      await Promise.race([logoutOpenIm(), pause(2000)]);
    } catch {
      // Best-effort; local session is already gone.
    }
    if (session?.accessToken) {
      try {
        await Promise.race([logoutSession(session.accessToken), pause(2000)]);
      } catch {
        // Best-effort server revoke.
      }
    }
    // Flip UI to the phone screen immediately — `actions.reset()` is async and otherwise
    // leaves `authorizationStateReady` mounted until `initGlobal` finishes.
    let nextGlobal = getGlobal();
    nextGlobal = {
      ...nextGlobal,
      currentUserId: undefined,
      // Must clear sync flags: the global sync callback skips when `isSynced` is still true,
      // so the next login never calls `loadAllChats` and the chat list spins forever.
      isSynced: false,
      isSyncing: false,
    };
    nextGlobal = updateAuth(nextGlobal, {
      state: 'authorizationStateWaitPhoneNumber',
      isLoading: false,
      errorKey: undefined,
      phoneNumber: undefined,
    });
    setGlobal(nextGlobal);
  }

  try {
    resetInitialLocationHash();
    resetLocationHash();
    await unsubscribe();
    await Promise.race([callApi('destroy'), pause(3000)]);
    await forceWebsync(false);
  } catch (err) {
    // Do nothing
  }

  actions.reset();
  await resetStorage();

  const targetAccountSlot = getFirstLoggedInAccountSlot() || 1;
  if (targetAccountSlot !== (ACCOUNT_SLOT || 1)) {
    window.location.replace(getAccountSlotUrl(targetAccountSlot));
    return;
  }

  if (payload?.forceInitApi) {
    actions.initApi();
  }
});

addActionHandler('requestChannelDifference', (global, actions, payload): ActionReturnType => {
  const { chatId } = payload;

  void callApi('requestChannelDifference', chatId);
});

addActionHandler('reset', async (global, actions): Promise<void> => {
  void cacheApi.clear(MEDIA_CACHE_NAME);
  void cacheApi.clear(MEDIA_CACHE_NAME_AVATARS);
  void cacheApi.clear(MEDIA_PROGRESSIVE_CACHE_NAME);
  void cacheApi.clear(CUSTOM_BG_CACHE_NAME);

  const hasAccounts = await resetStorage();
  destroySharedStatePort();

  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = Number((LANG_CACHE_NAME.match(/\d+$/) || ['0'])[0]);
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }

  updateAppBadge(0);

  if (hasAccounts) {
    return;
  }

  actions.initShared({ force: true });
  Object.values(global.byTabId).forEach(({ id: otherTabId, isMasterTab }) => {
    actions.init({ tabId: otherTabId, isMasterTab });
  });
});

function resetStorage() {
  if (resetStoragePromise) return resetStoragePromise;

  clearStoredSession(ACCOUNT_SLOT);
  const hasAccounts = Boolean(Object.values(getAccountsInfo()).length);
  const clearSharedStatePromise = hasAccounts ? Promise.resolve() : removeSharedStateFromCache();

  resetStoragePromise = Promise.all([
    clearEncryptedSession(),
    removeGlobalFromCache(),
    clearSharedStatePromise,
  ]).then(() => hasAccounts).finally(() => {
    resetStoragePromise = undefined;
  });

  return resetStoragePromise;
}

addActionHandler('disconnect', (): ActionReturnType => {
  void callApiLocal('disconnect');
});

addActionHandler('destroyConnection', (): ActionReturnType => {
  void callApiLocal('destroy', true, true);
});

// Bare ISO2 fallback for `loadNearestCountry`'s `DISABLE_REAL_MTPROTO` branch below, used only if
// the browser's own locale doesn't carry a region subtag at all (e.g. a bare `"en"`, no `"-US"`).
const DEFAULT_NEAREST_COUNTRY_ISO2 = 'CN';

addActionHandler('loadNearestCountry', async (global): Promise<void> => {
  // `fetchNearestCountry` (`GramJs.help.GetNearestDc`) is a real MTProto call gated on
  // `connectionState`, which -- like the country-list gap `readCountryList.ts` documents -- never
  // reaches `'connectionStateReady'` under `DISABLE_REAL_MTPROTO` (the gramjs worker is never
  // instantiated). Left as an early return, `global.auth.nearestCountry` would stay `undefined`
  // forever, and `CountryCodeInput`'s own loading spinner (gated on `!nearestCountry && !country`
  // in `AuthPhoneNumber.tsx`) would spin forever with it. There's no real way to geolocate without
  // a network call this build can't make, so this guesses from the browser's own locale region
  // subtag instead (e.g. `zh-CN` -> `CN`), falling back to a fixed default if the locale has none --
  // wrong sometimes, but always resolves, and the user can always search/pick a different country.
  if (DISABLE_REAL_MTPROTO) {
    const [, localeRegion] = navigator.language.split('-');
    global = updateAuth(global, {
      nearestCountry: (localeRegion || DEFAULT_NEAREST_COUNTRY_ISO2).toUpperCase(),
    });
    setGlobal(global);
    return;
  }

  if (global.connectionState !== 'connectionStateReady') {
    return;
  }

  const authNearestCountry = await callApi('fetchNearestCountry');

  global = getGlobal();
  global = updateAuth(global, {
    nearestCountry: authNearestCountry,
  });
  setGlobal(global);
});

addActionHandler('setDeviceToken', (global, actions, payload): ActionReturnType => {
  const { token } = payload;
  return {
    ...global,
    push: {
      deviceToken: token,
      subscribedAt: Date.now(),
    },
  };
});

addActionHandler('deleteDeviceToken', (global): ActionReturnType => {
  return {
    ...global,
    push: undefined,
  };
});

addActionHandler('lockScreen', async (global): Promise<void> => {
  const sessionJson = JSON.stringify({ ...loadStoredSession(), userId: global.currentUserId });
  const globalJson = serializeGlobal(global);
  const sharedStateJson = serializeShared(global.sharedState);

  await encryptSession(sessionJson, globalJson, sharedStateJson);
  forgetPasscode();
  clearStoredSession();
  updateAppBadge(0);

  global = getGlobal();
  global = updatePasscodeSettings(
    global,
    {
      isScreenLocked: true,
      invalidAttemptsCount: 0,
      timeoutUntil: undefined,
    },
  );
  setGlobal(global);

  setTimeout(() => {
    global = getGlobal();
    global = clearGlobalForLockScreen(global);
    setGlobal(global);
  }, LOCK_SCREEN_ANIMATION_DURATION_MS);

  try {
    await unsubscribe();
    await callApi('destroy', true);
  } catch (err) {
    // Do nothing
  }
});
